/**
 * Weather API client – thin wrappers around fetchWithAuth.
 *
 * Backend: GET /api/v2/sites/<siteId>/weather
 * Optional: ?refresh=true (manual refresh; backend enforces provider cooldown)
 */
import { fetchWithAuth } from './backendApi';

/**
 * Fetch normalized Signal Bridge weather for a site.
 *
 * @param {string} siteId
 * @param {{ refresh?: boolean }} [options]
 * @returns {Promise<object>} Backend JSON envelope { success, data, ... }
 */
export const getSiteWeather = (siteId, { refresh = false } = {}) => {
  const id = String(siteId || '').trim();
  if (!id) {
    return Promise.reject(new Error('siteId is required'));
  }

  const path = `/api/v2/sites/${encodeURIComponent(id)}/weather`;
  const url = refresh ? `${path}?refresh=true` : path;
  return fetchWithAuth(url, 'GET');
};

/**
 * Classify weather API failures from fetchWithAuth error messages.
 * @param {unknown} error
 * @returns {'WEATHER_LOCATION_MISSING' | 'NOT_FOUND' | 'UNAUTHORIZED' | 'OTHER'}
 */
export const classifyWeatherError = (error) => {
  const message = String(error?.message || error || '');
  if (message.includes('WEATHER_LOCATION_MISSING') || /weather location is not configured/i.test(message)) {
    return 'WEATHER_LOCATION_MISSING';
  }
  if (/\(404\)/.test(message) || /not found/i.test(message)) {
    return 'NOT_FOUND';
  }
  if (/\(401\)/.test(message) || /\(403\)/.test(message) || /not authenticated/i.test(message)) {
    return 'UNAUTHORIZED';
  }
  return 'OTHER';
};
